/**
 * How a `propose_site_locations` proposal is laid over the company's saved site set.
 *
 * In build-on-top mode each proposed row is either an edit of a saved site or a new one, and every
 * saved site no row claims is kept as it is. The committed set is kept rows plus proposed rows, and
 * a save replaces the whole draft — so a wrong match is not cosmetic. A row that fails to match its
 * site duplicates it, skewing every weight in its value-chain position; one that matches too much
 * drops sites the user never saw. The rules below choose a visible duplicate over a silent drop
 * every time it is unclear which happened.
 *
 * Pure and dependency-free so the rules are unit-tested (`site-locations-merge-spec.ts`).
 */
import type { CompanyGeolocationSite, GeolocationSiteSource } from '@upright/common/model/input/companies/geolocation';
import type { LocationExtentPrecision } from '@upright/common/model/input/companies/geolocation/api-types';
import { verdictAfterEdit } from '@upright/common/model/input/companies/geolocation/coordinate-check';
import { savedValuesInvalidatedByEdit } from '@upright/common/model/input/companies/geolocation/site-edits';

/**
 * One row as the chat-only tool echoes it. Kept here rather than imported from the backend module
 * so the UI bundle does not pull in zod or the server-only schema; structurally matches the item of
 * `ProposeSiteLocationsSchema` in `mcp/tools/propose-site-locations.ts`.
 */
export interface ProposedSite {
  rowId: string;
  /** The `id` of the saved site this row edits, as `get_site_locations` returned it. */
  existingSiteId?: number;
  name?: string;
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
  // Enum-constrained on the backend, but typed as string so an out-of-enum echo can be flagged
  // rather than crash the card.
  locationType: string;
  supplyChainPosition: string;
  latitude?: number | null;
  longitude?: number | null;
  /**
   * The verdict on the coordinates when the server looked the address up before the card rendered
   * (PLT-7875). Never the agent's: the tool schema does not take it.
   */
  geocodePrecision?: LocationExtentPrecision | null;
  geocodePartialMatch?: boolean | null;
  weight?: number | null;
  description?: string;
  evidenceUrl?: string;
}

/** A proposed row after the merge: fields it left unset are filled from the site it edits. */
export type MergedProposedSite = ProposedSite & {
  /** The saved site this row replaces, or null when it is a new site. */
  matchedExistingId: number | null;
  /**
   * The matched site's provenance. An edit keeps it: the column records where a row came from,
   * not whether someone has since edited it. Null for a new row, which takes the proposal's.
   */
  existingSource: GeolocationSiteSource | null;
  /**
   * Why a row that named an `existingSiteId` is saved as a new site anyway, or null when it named
   * none or was matched. Either way the row may well duplicate the site it meant to edit, which the
   * card says rather than leaving to a plain "New".
   *  - `not-found`: no saved site has that id. Site ids change whenever the set is saved, so this
   *    is usually an id from an earlier read.
   *  - `already-edited`: an earlier row in the proposal already edits that site.
   */
  unmatchedReason: UnmatchedSiteReason | null;
};

export type UnmatchedSiteReason = 'not-found' | 'already-edited';

export interface ProposalMerge {
  rows: MergedProposedSite[];
  /** Saved sites no proposed row claims; committed unchanged alongside the proposed rows. */
  keptExisting: CompanyGeolocationSite[];
}

/**
 * Normalised name|street|locality|country, used only as a fallback when a row names no
 * `existingSiteId`. Lowercased and trimmed so case and whitespace do not split one site in two.
 */
export function siteIdentityKey(parts: {
  name?: string | null;
  streetAddress?: string | null;
  addressLocality?: string | null;
  addressCountry?: string | null;
}): string {
  const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();
  return [norm(parts.name), norm(parts.streetAddress), norm(parts.addressLocality), norm(parts.addressCountry)].join(
    '|',
  );
}

const EMPTY_IDENTITY_KEY = '|||';

/** A row with no edit target: new, carrying exactly what the proposal said. */
function asNew(site: ProposedSite, unmatchedReason: UnmatchedSiteReason | null): MergedProposedSite {
  return { ...site, matchedExistingId: null, existingSource: null, unmatchedReason };
}

/**
 * Fill the fields a proposed edit left unset from the site it edits.
 *
 * The agent is shown only some fields of a saved site and told to omit what it is not changing,
 * so an absent field means "keep", not "clear". The exceptions are the fields whose saved value
 * stops being true when another field changes (`savedValuesInvalidatedByEdit`):
 *  - Coordinates belong to the address. When the proposal changes any part of it without giving a
 *    new pair, the saved pair is cleared rather than kept. With the address unchanged, a proposal
 *    that gives only one coordinate, or nulls, keeps the saved pair; coordinates always move as a
 *    pair, never as a new latitude with an old longitude.
 *  - A weight is cleared when the site moves to another value-chain position, unless the proposal
 *    gives one. With the position unchanged, a `null` weight keeps the saved one — the agent is
 *    told to leave weights null unless its source gives shares, so on an edit null almost always
 *    means "did not say".
 *  - Site type and value-chain position are required on every proposed row, so they always come
 *    from the proposal.
 *  - The geocoder's verdict goes with the coordinates it describes (PLT-7875): the saved site's
 *    while its saved pair stays, restated or not, and none with a new pair or a cleared one.
 * A user who does mean to clear a field can do it in the card, which shows the merged values.
 */
function asEdit(site: ProposedSite, existing: CompanyGeolocationSite): MergedProposedSite {
  const keep = (proposed: string | undefined, saved: string | null): string | undefined =>
    proposed !== undefined ? proposed : (saved ?? undefined);
  const proposalHasCoordinates = site.latitude != null && site.longitude != null;
  const invalidated = savedValuesInvalidatedByEdit(site, existing);
  const coordinate = (proposed: number | null | undefined, saved: number | null): number | null => {
    if (proposalHasCoordinates) return proposed ?? null;
    return invalidated.coordinates ? null : saved;
  };
  const latitude = coordinate(site.latitude, existing.latitude);
  const longitude = coordinate(site.longitude, existing.longitude);
  return {
    ...site,
    ...verdictAfterEdit(existing, { latitude, longitude }),
    name: keep(site.name, existing.name),
    streetAddress: keep(site.streetAddress, existing.streetAddress),
    addressLocality: keep(site.addressLocality, existing.addressLocality),
    addressRegion: keep(site.addressRegion, existing.addressRegion),
    postalCode: keep(site.postalCode, existing.postalCode),
    addressCountry: keep(site.addressCountry, existing.addressCountry),
    description: keep(site.description, existing.description),
    evidenceUrl: keep(site.evidenceUrl, existing.evidenceUrl),
    latitude,
    longitude,
    weight: site.weight != null ? site.weight : invalidated.weight ? null : existing.weight,
    matchedExistingId: existing.id,
    existingSource: existing.source,
    unmatchedReason: null,
  };
}

/**
 * Match proposed rows to saved sites for build-on-top.
 *
 *  1. A row naming an `existingSiteId` that is in the saved set, and not already claimed by an
 *     earlier row, edits that site. This is the match the agent is asked to make, because the
 *     identity key below breaks on the edits people actually make — a moved office, a renamed
 *     site, a country the agent normalised.
 *  2. A row still unmatched falls back to the identity key, but only when exactly one unclaimed
 *     saved site has that key. Discovered sites carry no name, so several in one city without a
 *     street share a key; picking one of them, or all, would be a guess, so the row is new instead.
 *     A key with every part blank identifies nothing and never matches.
 *  3. Everything else is new, and every saved site left unclaimed is kept.
 *
 * Each saved site is claimed at most once, in proposal order.
 */
export function mergeProposalWithExisting(proposed: ProposedSite[], existing: CompanyGeolocationSite[]): ProposalMerge {
  const byId = new Map(existing.map(site => [site.id, site]));
  const claimed = new Set<number>();
  const matches = new Map<string, CompanyGeolocationSite>();

  for (const site of proposed) {
    if (site.existingSiteId === undefined) continue;
    const target = byId.get(site.existingSiteId);
    if (target && !claimed.has(target.id)) {
      claimed.add(target.id);
      matches.set(site.rowId, target);
    }
  }

  for (const site of proposed) {
    if (matches.has(site.rowId)) continue;
    const key = siteIdentityKey(site);
    if (key === EMPTY_IDENTITY_KEY) continue;
    const candidates = existing.filter(saved => !claimed.has(saved.id) && siteIdentityKey(saved) === key);
    if (candidates.length === 1) {
      claimed.add(candidates[0].id);
      matches.set(site.rowId, candidates[0]);
    }
  }

  return {
    rows: proposed.map(site => {
      const target = matches.get(site.rowId);
      if (target) return asEdit(site, target);
      const reason: UnmatchedSiteReason | null =
        site.existingSiteId === undefined ? null : byId.has(site.existingSiteId) ? 'already-edited' : 'not-found';
      return asNew(site, reason);
    }),
    keptExisting: existing.filter(site => !claimed.has(site.id)),
  };
}

/**
 * Replace mode: the proposal is the whole new set, so nothing matches and nothing is kept. Kept
 * beside the build-on-top merge so the card builds its rows one way in both modes.
 */
export function proposalAsReplacement(proposed: ProposedSite[]): ProposalMerge {
  // An id means nothing here, so a row naming one is not "unmatched" — there was nothing to match.
  return { rows: proposed.map(site => asNew(site, null)), keptExisting: [] };
}
